import { useState } from 'react'
import { AdminLayout } from '@/layouts/admin-layout'
import { AppHeader } from '@/components/app-header'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Link, router } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { CheckIcon, XIcon, LightbulbIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { StarRating } from '@/components/ui/star-rating'

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }
type ProposedChange = { current: JsonValue; proposed: JsonValue }
type DiffRow = {
  type: 'same' | 'add' | 'remove'
  text: string
  oldLine: number | null
  newLine: number | null
}

interface Review {
  id: number
  score: number
  feedback: string | null
  improvement_suggestion: string | null
  dev_instance_id: number
  created_at: string
}

interface Improvement {
  id: number
  improvable_type: string
  improvable_id: number
  improvable_name: string | null
  proposed_changes: Record<string, ProposedChange>
  rationale: string
  source_review_ids: number[]
  source_reviews: Review[]
  status: string
  reviewed_by: string | null
  reviewed_at: string | null
  rejection_reason: string | null
  fields_changed_count: number
  created_at: string
}

interface Props {
  improvement: Improvement
}

const jsonValueToText = (value: JsonValue | undefined): string => {
  if (value === undefined || value === null) return ''
  if (typeof value === 'string') return value
  return JSON.stringify(value, null, 2)
}

const requiresJsonTextarea = (value: JsonValue | undefined): boolean =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const textToPrimitive = (value: string, originalValue: JsonValue | undefined): JsonValue => {
  if (typeof originalValue === 'number') {
    const parsed = Number(value)
    return Number.isNaN(parsed) ? value : parsed
  }
  if (typeof originalValue === 'boolean') {
    if (value === 'true') return true
    if (value === 'false') return false
  }
  if (originalValue === null && value === '') return null
  return value
}

const textToJsonValue = (value: string, originalValue: JsonValue | undefined): JsonValue => {
  if (requiresJsonTextarea(originalValue)) {
    try {
      return JSON.parse(value)
    } catch {
      return value
    }
  }

  return textToPrimitive(value, originalValue)
}

const hasInvalidJsonEdit = (change: ProposedChange, originalChange: ProposedChange | undefined): boolean =>
  Boolean(originalChange && requiresJsonTextarea(originalChange.proposed) && typeof change.proposed === 'string')

const splitLines = (value: string): string[] => value.replace(/\n$/, '').split('\n')

const buildLineDiff = (current: string, proposed: string): DiffRow[] => {
  const oldLines = splitLines(current)
  const newLines = splitLines(proposed)
  const dp = Array.from({ length: oldLines.length + 1 }, () => Array(newLines.length + 1).fill(0))

  for (let oldIndex = oldLines.length - 1; oldIndex >= 0; oldIndex -= 1) {
    for (let newIndex = newLines.length - 1; newIndex >= 0; newIndex -= 1) {
      dp[oldIndex][newIndex] = oldLines[oldIndex] === newLines[newIndex]
        ? dp[oldIndex + 1][newIndex + 1] + 1
        : Math.max(dp[oldIndex + 1][newIndex], dp[oldIndex][newIndex + 1])
    }
  }

  const rows: DiffRow[] = []
  let oldIndex = 0
  let newIndex = 0

  while (oldIndex < oldLines.length && newIndex < newLines.length) {
    if (oldLines[oldIndex] === newLines[newIndex]) {
      rows.push({
        type: 'same',
        text: oldLines[oldIndex],
        oldLine: oldIndex + 1,
        newLine: newIndex + 1
      })
      oldIndex += 1
      newIndex += 1
    } else if (dp[oldIndex + 1][newIndex] >= dp[oldIndex][newIndex + 1]) {
      rows.push({
        type: 'remove',
        text: oldLines[oldIndex],
        oldLine: oldIndex + 1,
        newLine: null
      })
      oldIndex += 1
    } else {
      rows.push({
        type: 'add',
        text: newLines[newIndex],
        oldLine: null,
        newLine: newIndex + 1
      })
      newIndex += 1
    }
  }

  while (oldIndex < oldLines.length) {
    rows.push({
      type: 'remove',
      text: oldLines[oldIndex],
      oldLine: oldIndex + 1,
      newLine: null
    })
    oldIndex += 1
  }

  while (newIndex < newLines.length) {
    rows.push({
      type: 'add',
      text: newLines[newIndex],
      oldLine: null,
      newLine: newIndex + 1
    })
    newIndex += 1
  }

  return rows
}

const diffRowClassName = (type: DiffRow['type']): string => {
  if (type === 'add') return 'bg-green-50 dark:bg-green-950/25'
  if (type === 'remove') return 'bg-red-50 dark:bg-red-950/25'
  return 'bg-background'
}

const diffMarkerClassName = (type: DiffRow['type']): string => {
  if (type === 'add') return 'text-green-700 dark:text-green-300'
  if (type === 'remove') return 'text-red-700 dark:text-red-300'
  return 'text-muted-foreground'
}

function UnifiedTextDiff({ current, proposed }: { current: string; proposed: string }) {
  const rows = buildLineDiff(current, proposed)
  const addedLines = rows.filter((row) => row.type === 'add').length
  const removedLines = rows.filter((row) => row.type === 'remove').length

  return (
    <div className="mb-4 overflow-hidden rounded-md border">
      <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-2">
        <Label className="text-xs text-muted-foreground">Diff</Label>
        <div className="flex items-center gap-2 text-xs">
          {removedLines > 0 && <span className="text-red-700 dark:text-red-300">-{removedLines}</span>}
          {addedLines > 0 && <span className="text-green-700 dark:text-green-300">+{addedLines}</span>}
        </div>
      </div>
      <div className="max-h-[520px] overflow-auto font-mono text-xs">
        {rows.map((row, index) => (
          <div key={index} className={`grid grid-cols-[3rem_3rem_1.5rem_minmax(0,1fr)] ${diffRowClassName(row.type)}`}>
            <span className="select-none border-r px-2 py-0.5 text-right text-muted-foreground">
              {row.oldLine ?? ''}
            </span>
            <span className="select-none border-r px-2 py-0.5 text-right text-muted-foreground">
              {row.newLine ?? ''}
            </span>
            <span className={`select-none px-1 py-0.5 text-center ${diffMarkerClassName(row.type)}`}>
              {row.type === 'add' ? '+' : row.type === 'remove' ? '-' : ''}
            </span>
            <pre className="whitespace-pre-wrap break-words px-2 py-0.5">{row.text || ' '}</pre>
          </div>
        ))}
      </div>
    </div>
  )
}

interface ProposedValueEditorProps {
  value: JsonValue
  originalValue: JsonValue | undefined
  invalidJson: boolean
  onChange: (value: JsonValue) => void
}

function ProposedValueEditor({ value, originalValue, invalidJson, onChange }: ProposedValueEditorProps) {
  if (Array.isArray(value)) {
    const originalArray = Array.isArray(originalValue) ? originalValue : []

    return (
      <div className="space-y-2">
        {value.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              value={jsonValueToText(item)}
              onChange={(event) => {
                const next = [...value]
                next[index] = textToJsonValue(event.target.value, originalArray[index] ?? originalArray[0])
                onChange(next)
              }}
              className="h-8 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900 font-mono text-sm"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
            >
              <Trash2Icon className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => onChange([...value, ''])}>
          <PlusIcon className="mr-2 h-4 w-4" />
          Add item
        </Button>
      </div>
    )
  }

  if (typeof originalValue === 'boolean') {
    return (
      <Select value={String(value)} onValueChange={(nextValue) => onChange(nextValue === 'true')}>
        <SelectTrigger className="h-8 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">true</SelectItem>
          <SelectItem value="false">false</SelectItem>
        </SelectContent>
      </Select>
    )
  }

  if (typeof originalValue === 'number') {
    return (
      <Input
        type="number"
        value={typeof value === 'number' ? value : ''}
        onChange={(event) => onChange(textToPrimitive(event.target.value, originalValue))}
        className="h-8 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900 font-mono text-sm"
      />
    )
  }

  return (
    <>
      <Textarea
        value={jsonValueToText(value)}
        onChange={(event) => onChange(textToJsonValue(event.target.value, originalValue))}
        className="min-h-[100px] max-h-[360px] bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900 text-sm font-mono"
        rows={Math.min(14, Math.max(4, jsonValueToText(value).split('\n').length + 1))}
      />
      {invalidJson && (
        <p className="mt-1 text-xs text-destructive">
          This field must be valid JSON before the improvement can be accepted.
        </p>
      )}
    </>
  )
}

function Show({ improvement }: Props) {
  const { t } = useTranslation('admin')
  const [isAccepting, setIsAccepting] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [editedChanges, setEditedChanges] = useState<Record<string, ProposedChange>>(
    improvement.proposed_changes
  )

  const isPending = improvement.status === 'pending'
  const basePath = '/admin/collective_knowledge/improvements'
  const targetPath = improvement.improvable_type === 'Gotcha'
    ? `/admin/collective_knowledge/gotchas/${improvement.improvable_id}`
    : `/admin/collective_knowledge/manuals/${improvement.improvable_id}`
  const hasInvalidJsonChanges = Object.entries(editedChanges).some(([field, changes]) =>
    hasInvalidJsonEdit(changes, improvement.proposed_changes[field])
  )

  const handleAccept = () => {
    if (hasInvalidJsonChanges) return

    setIsAccepting(true)
    router.post(`${basePath}/${improvement.id}/accept`, {
      proposed_changes: editedChanges
    }, {
      onFinish: () => setIsAccepting(false)
    })
  }

  const handleReject = () => {
    setIsRejecting(true)
    router.post(`${basePath}/${improvement.id}/reject`, {
      reason: rejectionReason
    }, {
      onFinish: () => {
        setIsRejecting(false)
        setRejectDialogOpen(false)
        setRejectionReason('')
      }
    })
  }

  const statusVariant: Record<string, 'warning' | 'success' | 'destructive'> = {
    pending: 'warning', accepted: 'success', rejected: 'destructive',
  }

  return (
    <>
      <AppHeader breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: t('collectiveKnowledge.improvements.title'), href: basePath },
        { label: improvement.improvable_name || `#${improvement.improvable_id}` }
      ]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-3xl font-bold text-foreground">
                      {improvement.improvable_name || `#${improvement.improvable_id}`}
                    </h1>
                    <Badge variant={statusVariant[improvement.status] || 'secondary'}>
                      {t(`collectiveKnowledge.improvements.status.${improvement.status}`)}
                    </Badge>
                    <Badge variant="secondary">{improvement.improvable_type}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    <Link href={targetPath} className="text-blue-600 hover:text-blue-900">
                      {t('collectiveKnowledge.improvements.show.viewTarget')}
                    </Link>
                  </p>
                </div>
                <Button variant="outline" asChild>
                  <Link href={basePath}>{t('collectiveKnowledge.improvements.buttons.backToList')}</Link>
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content (2/3) */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Proposed Changes */}
                  {Object.entries(editedChanges).map(([field, changes]) => {
                    const showTextDiff = typeof changes.current === 'string' &&
                      typeof changes.proposed === 'string' &&
                      changes.current !== changes.proposed

                    return (
                      <Card key={field} className="shadow-none">
                        <CardHeader>
                          <CardTitle className="text-base capitalize flex items-center gap-2">
                            {field.replace(/_/g, ' ')}
                            {isPending && <PencilIcon className="h-3.5 w-3.5 text-muted-foreground" />}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {showTextDiff && (
                            <UnifiedTextDiff current={changes.current as string} proposed={changes.proposed as string} />
                          )}

                          {showTextDiff ? (
                            <div>
                              <Label className="text-xs text-muted-foreground mb-1 block">
                                {t('collectiveKnowledge.improvements.show.proposed')}
                              </Label>
                              {isPending ? (
                                <ProposedValueEditor
                                  value={changes.proposed}
                                  originalValue={improvement.proposed_changes[field]?.proposed}
                                  invalidJson={hasInvalidJsonEdit(changes, improvement.proposed_changes[field])}
                                  onChange={(value) => setEditedChanges(prev => ({
                                    ...prev,
                                    [field]: { ...prev[field], proposed: value }
                                  }))}
                                />
                              ) : (
                                <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-md border border-green-200 dark:border-green-900">
                                  <pre className="text-sm whitespace-pre-wrap break-words">{jsonValueToText(changes.proposed) || '-'}</pre>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-xs text-muted-foreground mb-1 block">
                                  {t('collectiveKnowledge.improvements.show.current')}
                                </Label>
                                <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-md border border-red-200 dark:border-red-900">
                                  <pre className="text-sm whitespace-pre-wrap break-words">{jsonValueToText(changes.current) || '-'}</pre>
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground mb-1 block">
                                  {t('collectiveKnowledge.improvements.show.proposed')}
                                </Label>
                                {isPending ? (
                                  <ProposedValueEditor
                                    value={changes.proposed}
                                    originalValue={improvement.proposed_changes[field]?.proposed}
                                    invalidJson={hasInvalidJsonEdit(changes, improvement.proposed_changes[field])}
                                    onChange={(value) => setEditedChanges(prev => ({
                                      ...prev,
                                      [field]: { ...prev[field], proposed: value }
                                    }))}
                                  />
                                ) : (
                                  <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-md border border-green-200 dark:border-green-900">
                                    <pre className="text-sm whitespace-pre-wrap break-words">{jsonValueToText(changes.proposed) || '-'}</pre>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )
                  })}

                  {/* Rationale */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>{t('collectiveKnowledge.improvements.show.rationale')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm">{improvement.rationale}</p>
                    </CardContent>
                  </Card>

                  {/* Rejection Reason */}
                  {improvement.rejection_reason && (
                    <Card className="shadow-none border-destructive">
                      <CardHeader>
                        <CardTitle className="text-destructive">
                          {t('collectiveKnowledge.improvements.show.rejectionReason')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm">{improvement.rejection_reason}</p>
                        {improvement.reviewed_by && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {t('collectiveKnowledge.improvements.show.reviewedBy', {
                              name: improvement.reviewed_by,
                              date: improvement.reviewed_at ? new Date(improvement.reviewed_at).toLocaleDateString() : 'N/A'
                            })}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Sidebar (1/3) */}
                <div className="space-y-6">
                  {/* Actions */}
                  {improvement.status === 'pending' && (
                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle>{t('collectiveKnowledge.improvements.show.actions')}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <Button
                          className="w-full justify-start"
                          onClick={handleAccept}
                          disabled={isAccepting || hasInvalidJsonChanges}
                        >
                          <CheckIcon className="mr-2 h-4 w-4" />
                          {isAccepting
                            ? t('collectiveKnowledge.improvements.buttons.accepting')
                            : t('collectiveKnowledge.improvements.buttons.accept')
                          }
                        </Button>

                        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-red-600">
                              <XIcon className="mr-2 h-4 w-4" />
                              {t('collectiveKnowledge.improvements.buttons.reject')}
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>{t('collectiveKnowledge.improvements.show.rejectTitle')}</DialogTitle>
                              <DialogDescription>
                                {t('collectiveKnowledge.improvements.show.rejectDescription')}
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label htmlFor="reason">{t('collectiveKnowledge.improvements.show.reason')}</Label>
                                <Textarea
                                  id="reason"
                                  value={rejectionReason}
                                  onChange={(e) => setRejectionReason(e.target.value)}
                                  placeholder={t('collectiveKnowledge.improvements.show.reasonPlaceholder')}
                                  rows={3}
                                />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                                {t('collectiveKnowledge.improvements.buttons.cancel')}
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={handleReject}
                                disabled={isRejecting || !rejectionReason.trim()}
                              >
                                {isRejecting
                                  ? t('collectiveKnowledge.improvements.buttons.rejecting')
                                  : t('collectiveKnowledge.improvements.buttons.reject')
                                }
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </CardContent>
                    </Card>
                  )}

                  {/* Source Reviews */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{t('collectiveKnowledge.improvements.show.sourceReviews')}</span>
                        {improvement.source_reviews.length > 0 && (
                          <span className="text-sm font-normal text-muted-foreground">
                            ({improvement.source_reviews.length})
                          </span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {improvement.source_reviews.length > 0 ? (
                        <div className="space-y-3">
                          {improvement.source_reviews.map((review) => (
                            <div key={review.id} className="border-b border-border last:border-0 pb-2 last:pb-0">
                              <div className="flex items-center justify-between mb-1">
                                <StarRating value={review.score} />
                                <span className="text-[10px] text-muted-foreground">
                                  {new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              </div>
                              {review.feedback && (
                                <p className="text-xs text-muted-foreground">{review.feedback}</p>
                              )}
                              {review.improvement_suggestion && (
                                <div className="mt-1 p-1.5 bg-blue-50 dark:bg-blue-950/20 rounded flex items-start gap-1.5">
                                  <LightbulbIcon className="h-3 w-3 text-blue-500 mt-0.5 shrink-0" />
                                  <span className="text-xs text-blue-700 dark:text-blue-400">{review.improvement_suggestion}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {t('collectiveKnowledge.improvements.show.noSourceReviews')}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Info */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>{t('collectiveKnowledge.improvements.show.info')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <dl className="space-y-3 text-sm">
                        <div>
                          <dt className="text-muted-foreground">{t('collectiveKnowledge.improvements.show.targetType')}</dt>
                          <dd><Badge variant="secondary">{improvement.improvable_type}</Badge></dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">{t('collectiveKnowledge.improvements.show.fieldsChanged')}</dt>
                          <dd>{improvement.fields_changed_count}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">{t('collectiveKnowledge.improvements.show.created')}</dt>
                          <dd>{new Date(improvement.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</dd>
                        </div>
                        {improvement.reviewed_by && (
                          <div>
                            <dt className="text-muted-foreground">{t('collectiveKnowledge.improvements.show.reviewedByLabel')}</dt>
                            <dd>{improvement.reviewed_by}</dd>
                          </div>
                        )}
                      </dl>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
